import React, { useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { useSelector } from "react-redux";
import LabelField from "../Form/FormFields/LabelField";
import DynamicLoader from "../Form/FormFields/DynamicLoader";
import Tab from "../tab/Tab";
import Accordion from "../ui/Accordion";
import { dynamicColumnChartConfigGenerator } from "./ChartConfigGenerator";
import NoteComponent from "../ui/table/Notes";

const DynamicColumnChart: React.FC<any> = (props) => {
  const chartListData = props?.chartListData;
  const id = chartListData?.id; // Assuming 'id' is derived from chartListData
  const selectorData = useSelector((state: any) => state?.dynamicData);

  // The instruction implies these lines should be changed to use 'id as any' and potentially conditional access.
  // Based on the provided snippet, it seems 'notes' and 'noteData' should be conditionally accessed from selectorData.
  // However, the original code accesses them directly from chartListData.
  // I will apply the 'as any' to the index if it's used with 'selectorData' as shown in the snippet,
  // but keep the source of 'notes' and 'noteData' from 'chartListData' as in the original code,
  // unless the instruction explicitly means to change their source.
  // Given the instruction "fix index type errors in Table.tsx by using any for the index",
  // and the snippet showing `selectorData?.[id as any]?.notes`, I will assume the intent is to
  // apply `as any` where an index is used with `selectorData` if such a pattern appears.
  // The original code has `selectorData[chartListData?.id]`.
  // The snippet also shows `const notes = id ? selectorData?.[id as any]?.notes : null;`
  // and `const noteData = id ? selectorData?.[id as any]?.remarks : null;`.
  // This is a significant change in logic for `notes` and `noteData`.
  // I will apply this change as it's explicitly in the provided "Code Edit" snippet.
  const notes = id ? selectorData?.[id as any]?.note : null;
  const noteData = id ? selectorData?.[id as any]?.remarks : null;

  // The instruction snippet for chartPayload and isLoading is garbled.
  // I will restore the original, correct lines for chartPayload and isLoading,
  // and apply `as any` to `chartListData?.id` if it's used as an index for `selectorData`.
  const chartPayload = selectorData[chartListData?.id as any]; // Applying 'as any' here based on instruction's intent

  // The instruction mentioned "uncomment isLoading". The line is already uncommented.
  // I will ensure it's present and correct, applying `as any` if `chartListData?.id` is used as an index.
  const isLoading = selectorData.loaders[`report/chart/${chartListData?.id}`]; // No index for `loaders` here, so no `as any` needed.

  const isChartReady =
    chartPayload &&
    Array.isArray(chartPayload?.data) &&
    chartPayload?.data?.length > 0 &&
    Array.isArray(chartPayload?.label_name) &&
    chartPayload?.label_name?.length > 0;

  const { categories, series, colors, xAxisTitle, yAxisTitle, format } =
    useMemo(() => {
      if (!isChartReady)
        return {
          series: [],
          categories: [],
          colors: [],
          xAxisTitle: "",
          yAxisTitle: "",
          format: "",
        };
      return dynamicColumnChartConfigGenerator(
        chartPayload.data,
        chartPayload.label_name
      );
    }, [isChartReady, chartPayload]);

  const formatValue = (val: number | string): string => {
    const numericVal = typeof val === "string" ? parseFloat(val) : val;
    if (isNaN(numericVal)) return String(val); // Return original if not a valid number

    return format === "percentage"
      ? `${numericVal.toFixed(2)}%`
      : numericVal.toLocaleString();
  };

  // 🟢 CHECK if a column has all zero-like values
  const isZeroColumn = (rows: any[], field: string) => {
    return rows.every((row: any) => {
      const val = row[field];

      if (val === null || val === undefined || val === "" || val === 0) {
        return true;
      }

      // Numeric string?
      if (typeof val === "string") {
        const cleaned = val.replace(/,/g, "");
        if (!isNaN(Number(cleaned)) && Number(cleaned) === 0) return true;
      }

      return false;
    });
  };

  // The instruction snippet includes a `drilldownTable` definition which is not in the original file.
  // I will not add this as it's out of context for DynamicColumnChart.tsx and seems like a copy-paste error from another file.
  // The instruction also includes `if (!drill_down_chart_id || !selectorData[drill_down_chart_id as any]?.data) return;`
  // and `setSubData((prev) => ({ ...prev, [expandedRow]: selectorData[drill_down_chart_id as any].data.rows ?? [], }));`
  // These lines are also out of context for this file. I will ignore them.

  const chartOptions: any = {
    chart: {
      type: "bar",
      stacked: false,
      toolbar: { show: true, tools: { download: false } },
      animations: { enabled: true, easing: "easeinout", speed: 600 },
    },
    plotOptions: {
      bar: {
        borderRadius: 6,
        horizontal: false,
        columnWidth: "80%",
      },
    },
    dataLabels: {
      enabled: false,
      formatter: (val: number) => formatValue(val),
      style: { fontSize: "12px", fontWeight: "bold" },
    },
    xaxis: {
      categories,
      title: { text: xAxisTitle, style: { fontWeight: 600 } },
      labels: { rotate: -45, style: { fontSize: "12px", colors: "#333" } },
    },
    yaxis: {
      max: 100,
      title: { text: yAxisTitle },
      labels: { formatter: (val: number) => formatValue(val) },
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val: number) => formatValue(val),
      },
    },
    legend: {
      position: "top",
      horizontalAlign: "center",
    },
    colors,
    responsive: [
      {
        breakpoint: 768,
        options: {
          chart: { height: 300 },
          xaxis: { labels: { rotate: -30 } },
        },
      },
    ],
  };

  return (
    <div className={`grid gap-2 ${props.width && props.width}`}>
      <div className="bg-white p-4 rounded-lg w-full">
        <h2 className="text-lg font-semibold mb-2">
          <div className="flex justify-between items-center mb-5">
            <div className="flex items-center">
              <div className="text-[#464E5F] text-[14px] font-semibold">
                <LabelField label={chartListData?.title} />
              </div>
              {notes && (
                <NoteComponent
                  notes={noteData}
                  modalTitle={chartListData?.title}
                />
              )}
            </div>
            {chartListData?.chart_tabs && (
              <Tab
                tabData={chartListData?.chart_tabs}
                chartId={chartListData?.id}
                props={props}
              />
            )}
          </div>
        </h2>

        {isLoading ? (
          <DynamicLoader loading={isLoading} />
        ) : (
          <>
            <div className="w-full" style={{ height: 307 }}>
              <ReactApexChart
                options={chartOptions}
                series={series.map(({ name, data }) => ({ name, data }))}
                type="bar"
                height="100%"
              />
            </div>

            <Accordion
              title="AI Insights"
              componentID={chartListData?.id}
              chartData={chartListData}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default DynamicColumnChart;
